import React from 'react';
import { 
  Dialog, 
  DialogContent, 
  Box, 
  Typography, 
  Divider,
  Alert,
  useMediaQuery,
  useTheme
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WaveLoadingAnimation from './WaveLoadingAnimation';
import ProgressFacts from './ProgressFacts';

// Define the progress step type (kept for backward compatibility)
export interface ProgressStep {
  id: string;
  label: string;
  completed: boolean;
}

interface ProgressModalProps {
  open: boolean;
  title: string;
  subtitle: string;
  steps?: ProgressStep[];
  currentStep?: string | null;
  type?: 'simulation' | 'ideaGen';
}

const ProgressModal: React.FC<ProgressModalProps> = ({
  open,
  title,
  subtitle,
  steps,
  currentStep,
  type = 'simulation'
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 2,
          backgroundColor: 'background.paper',
          // Ensure modal doesn't overflow on mobile
          margin: { xs: 2, sm: 4 },
          maxHeight: { xs: '90vh', sm: '80vh' },
          overflow: 'auto'
        }
      }}
    >
      <DialogContent sx={{ 
        py: { xs: 3, sm: 4 },
        px: { xs: 2, sm: 3 },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}>
        {/* Wave Animation */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center',
          mb: 3 
        }}>
          <WaveLoadingAnimation 
            size={isMobile ? 'small' : 'large'} 
          />
        </Box>

        {/* Main Status Text */}
        <Typography 
          variant="h6" 
          align="center"
          gutterBottom
          sx={{ 
            mb: 2,
            fontSize: { xs: '1.1rem', sm: '1.25rem' },
            fontWeight: 500
          }}
        >
          {title}
        </Typography>

        {/* Subtitle (if different from title) */}
        {subtitle && subtitle !== title && (
          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{ mb: 3 }}
          >
            {subtitle}
          </Typography>
        )}

        {/* Rotating Facts */}
        <ProgressFacts type={type} />

        {/* Divider */}
        <Divider sx={{ 
          my: 2, 
          width: '100%',
          opacity: 0.3 
        }} />

        {/* AI Disclaimer */}
        <Alert 
          severity="info" 
          icon={<InfoOutlinedIcon fontSize="small" />}
          sx={{ 
            py: { xs: 0.5, sm: 1 },
            px: { xs: 1.5, sm: 2 },
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            backgroundColor: (theme) => 
              theme.palette.mode === 'dark' 
                ? 'rgba(33, 150, 243, 0.05)' 
                : 'rgba(33, 150, 243, 0.08)',
            border: '1px solid',
            borderColor: (theme) =>
              theme.palette.mode === 'dark'
                ? 'rgba(33, 150, 243, 0.15)'
                : 'rgba(33, 150, 243, 0.2)',
            '& .MuiAlert-icon': {
              fontSize: { xs: '1rem', sm: '1.25rem' },
              opacity: 0.9
            },
            '& .MuiAlert-message': {
              padding: 0
            }
          }}
        >
          AI-generated insights may contain inaccuracies. Always review and verify recommendations before implementation.
        </Alert>
      </DialogContent>
    </Dialog>
  );
};

export default ProgressModal; 